import type { InputHTMLAttributes } from 'react';
import { useId } from 'react';
import { ICONO, type Icono } from '../ui/iconos.js';

/**
 * El campo del paciente (field de la referencia): rótulo grande, caja de 60 px y
 * texto de 19 px.
 *
 * Es el `Campo` del sistema llevado a la escala de este público: el rótulo es un
 * `label` real asociado por `htmlFor` (no un placeholder que desaparece al
 * escribir), la ayuda y el error se anuncian con `aria-describedby`, y el error
 * además con `role="alert"`. El foco se ve dos veces: contorno navy y halo
 * navy-100 alrededor de la caja entera, no sólo del texto.
 *
 * El error usa `destructive`, **no el rojo del semáforo**: un campo mal
 * completado no es un estado clínico.
 */
export function CampoGrande({
  etiqueta,
  ayuda,
  error,
  icono: IconoDelCampo,
  className = '',
  ...resto
}: InputHTMLAttributes<HTMLInputElement> & {
  etiqueta: string;
  ayuda?: string | undefined;
  error?: string | undefined;
  icono?: Icono;
}) {
  const id = useId();
  const idAyuda = `${id}-ayuda`;
  const idError = `${id}-error`;
  const descrito = [ayuda ? idAyuda : null, error ? idError : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1">
      <label className="text-lg font-bold text-heading" htmlFor={id}>
        {etiqueta}
      </label>
      <div
        className={`flex min-h-15 items-center gap-3 rounded-lg bg-card px-4 outline-2 -outline-offset-2 focus-within:shadow-halo-navy-100 focus-within:outline-ring ${
          error ? 'outline-destructive' : 'outline-input'
        }`}
      >
        {IconoDelCampo ? (
          <IconoDelCampo
            aria-hidden="true"
            className={`${ICONO.normal} shrink-0 text-muted-foreground`}
          />
        ) : null}
        <input
          aria-invalid={error ? true : undefined}
          className={`min-w-0 flex-1 border-0 bg-transparent text-touch text-heading outline-none focus-visible:outline-none ${className}`}
          id={id}
          {...(descrito ? { 'aria-describedby': descrito } : {})}
          {...resto}
        />
      </div>
      {ayuda ? (
        <p className="text-base text-subtle" id={idAyuda}>
          {ayuda}
        </p>
      ) : null}
      {error ? (
        <p className="text-base font-bold text-destructive-fg" id={idError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
